import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

const Modal = ({
  title,
  onClose,
  onConfirm,
  children,
  isOpen = true,
  singleButton = false,
  confirmText = "Confirm",
  cancelText = "Cancel",
  widthClassName = "max-w-md",
  isConfirmDisabled = false,
  hideFooter = false,
  closeOnBackdropClick = true,
  showCloseX = true,
  confirmButtonColor = "primary",
  cancelButtonColor = "gray",
  className = "",
}) => {
  // Close modal on Escape key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (event) => {
      if (event.key === "Escape" && onClose) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose, isOpen]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Don't render if modal is not open
  if (!isOpen) return null;

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && closeOnBackdropClick && onClose) {
      onClose();
    }
  };

  // Handle close button click
  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClose) {
      onClose();
    }
  };

  // Handle confirm button click
  const handleConfirmClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onConfirm) {
      onConfirm();
    }
  };

  // Get button color classes
  const getButtonColorClasses = (color, isDisabled = false) => {
    if (isDisabled) {
      return "bg-slate-300 text-slate-500 cursor-not-allowed";
    }

    const colorMap = {
      primary:
        "bg-primary-500 hover:bg-primary-600 text-white focus:ring-primary-500",
      green:
        "bg-emerald-500 hover:bg-emerald-600 text-white focus:ring-emerald-500",
      orange:
        "bg-orange-500 hover:bg-orange-600 text-white focus:ring-orange-500",
      red: "bg-red-500 hover:bg-red-600 text-white focus:ring-red-500",
      blue: "bg-blue-500 hover:bg-blue-600 text-white focus:ring-blue-500",
      gray: "bg-slate-500 hover:bg-slate-600 text-white focus:ring-slate-500",
    };

    return colorMap[color] || colorMap.primary;
  };

  // Render modal directly in the DOM (no portal)
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50"></div>

      {/* Modal Content */}
      <div
        className={clsx(
          "relative bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col",
          widthClassName,
          className
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-slate-200 flex-shrink-0">
            <h2
              id="modal-title"
              className="text-xl font-semibold text-slate-900"
            >
              {title}
            </h2>
            {showCloseX && (
              <button
                type="button"
                onClick={handleCloseClick}
                className="p-1 rounded-lg hover:bg-slate-100 transition-colors duration-200 flex-shrink-0"
                aria-label="Close modal"
              >
                <FontAwesomeIcon
                  icon="times"
                  className="text-slate-500 w-5 h-5"
                />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {/* Footer */}
        {!hideFooter && (
          <div className="flex justify-end space-x-3 p-6 border-t border-slate-200 flex-shrink-0">
            {!singleButton && (
              <button
                type="button"
                onClick={handleCloseClick}
                className={clsx(
                  "px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-4",
                  getButtonColorClasses(cancelButtonColor)
                )}
              >
                {cancelText}
              </button>
            )}
            {onConfirm && (
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={isConfirmDisabled}
                className={clsx(
                  "px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-4",
                  getButtonColorClasses(confirmButtonColor, isConfirmDisabled)
                )}
              >
                {confirmText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
